// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2025 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { GetShipTosApiParameter } from "@insite/client-framework/Services/CustomersService";
import loadShipTos from "@insite/client-framework/Store/Data/ShipTos/Handlers/LoadShipTos";
import { getShipTosDataView } from "@insite/client-framework/Store/Data/ShipTos/ShipTosSelectors";
import translate from "@insite/client-framework/Translate";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import Button, { ButtonPresentationProps } from "@insite/mobius/Button";
import GridContainer from "@insite/mobius/GridContainer";
import GridItem, { GridItemProps } from "@insite/mobius/GridItem";
import Tag, { TagPresentationProps } from "@insite/mobius/Tag";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import loadOpenBackOrders from "@steiner/App/Handlers/XNG/OpenBackOrders/LoadOpenBackOrders";
import { CustomAppState } from "@steiner/App/Store/Reducers";
import { GridContainerProps } from "@steiner/MobiusOverrides/GridContainer/GridContainer";
import Select from "@steiner/MobiusOverrides/Select/Select";
import TextField from "@steiner/MobiusOverrides/TextField";
import { XNGAGE_ELEMENTS } from "@steiner/XDL/constants";
import debounce from "lodash/debounce"
import React, { useCallback, useEffect, useState } from "react";
import { FC } from "react";
import { connect, ResolveThunks } from "react-redux";

const enum fields {
    showTagsOnly = "showTagsOnly",
}

interface OwnProps extends WidgetProps {
    fields: {
        [fields.showTagsOnly]: boolean;
    };
}

export interface OpenBackOrdersFilterStyles {
    container?: GridContainerProps;
    gridItemStyle?: GridItemProps;
    buttonsItem?: GridItemProps;
    clearFiltersButton?: ButtonPresentationProps;
    appliedFiltersContainer?: InjectableCss;
    appliedFilterTag?: TagPresentationProps;
}

export const openBackOrdersFilterStyles: OpenBackOrdersFilterStyles = {};

const styles = openBackOrdersFilterStyles;

const shipTosParameter: GetShipTosApiParameter = {
    page: 1,
    pageSize: 20,
    expand: ["validation", "excludeShowAll", "excludeOneTime"],
};

const mapStateToProps = (state: CustomAppState) => {
    return {
        shipTosDataView: getShipTosDataView(state, shipTosParameter),
        filters: state.xOpenBackOrders.getOpenBackOrdersParameter,
    };
};

const mapDispatchToProps = {
    loadOpenBackOrders,
    loadShipTos,
};

type Props = OwnProps & ReturnType<typeof mapStateToProps> & ResolveThunks<typeof mapDispatchToProps>;

export const OpenBackOrdersFilter: FC<Props> = ({
    loadShipTos,
    loadOpenBackOrders,
    shipTosDataView,
    fields,
    filters,
}) => {
    const [filter, setFilter] = useState({
        shipToAddress: "-1",
        orderNumber: "",
        releaseNumber: "",
        orderedBy: "",
        partNumber: "",
        description: "",
    });

    useEffect(() => {
        if (!shipTosDataView.value && !shipTosDataView.isLoading) {
            loadShipTos(shipTosParameter);
        }
        loadOpenBackOrders({ ...filter });
    }, []);

    useEffect(() => {
        if (filters) {
            setFilter(filters as any); // Sync local state with Redux state
        }
    }, [filters]);

    const changeHandler = (field: string, value: string) => {
        const updatedFilter = {
            ...filter,
            [field]: value,
        };

        setFilter(updatedFilter);
        debouncedSearch(updatedFilter);
    };

    const clearFilters = () => {
        const updatedFilter = {
            shipToAddress: "-1",
            orderNumber: "",
            releaseNumber: "",
            orderedBy: "",
            partNumber: "",
            description: "",
        };

        setFilter(updatedFilter);
        loadOpenBackOrders({ ...updatedFilter });
    };

    const debouncedSearch = useCallback(
        debounce((filter: any) => {
            loadOpenBackOrders({ ...filter });
        }, 250),
        [loadOpenBackOrders]
    );


    const onDeleteFilter = (filterKey: string, filterValue?: string) => {
        changeHandler(filterKey, filterValue || filterKey === "shipToAddress" ? "-1" : "");
    };

    const SearchTags = ({
        filters,
        onDelete,
    }: {
        filters: Record<string, string | undefined>;
        onDelete: (filterKey: string, filterValue?: string) => void;
    }) => {
        const translateLabel = (key: string, value: string) => {
            const labels: Record<string, string> = {
                shipToAddress: translate("Ship To Address"),
                orderNumber: translate("Order # / PO #"),
                releaseNumber: translate("Release #"),
                orderedBy: translate("Ordered By"),
                partNumber: translate("Part Number"),
                description: translate("Description"),
            };
            return `${labels[key] || key}: ${value}`;
        };

        return (
            <StyledWrapper {...styles.appliedFiltersContainer}>
                {Object.entries(filters).map(([key, value]) =>
                    value ? (
                        <Tag
                            key={key}
                            {...styles.appliedFilterTag}
                            data-test-selector={`filterTag_${key}`}
                            onDelete={() => onDelete(key, key === "customerSequence" ? "-1" : undefined)}
                        >
                            {translateLabel(key, value)}
                        </Tag>
                    ) : null,
                )}
            </StyledWrapper>
        );
    };

    if (fields.showTagsOnly) {
        return (
            <SearchTags
                filters={{
                    shipToAddress: !filters?.shipToAddress
                        ? translate("Billing Address")
                        : filters?.shipToAddress !== "-1"
                            ? filters?.shipToAddress
                            : undefined,
                    orderNumber: filters?.orderNumber,
                    releaseNumber: filters?.releaseNumber,
                    orderedBy: filters?.orderedBy,
                    partNumber: filters?.partNumber,
                    description: filters?.description,
                }}
                onDelete={onDeleteFilter}
            />
        );
    }

    return (
        <GridContainer {...styles.container} data-test-selector="account-summary-filters">
            <GridItem {...styles.gridItemStyle}>
                <Select
                    value={filter.shipToAddress}
                    label={translate("Ship To Address")}
                    labelPosition="top"
                    onChange={e => {
                        changeHandler("shipToAddress", e.target.value);
                    }}
                    data-test-selector="openBarckOrders_shipToFilter"
                >
                    <option value="-1">{translate("Show All")}</option>
                    {shipTosDataView.value &&
                        shipTosDataView.value.map(shipTo => (
                            <option key={shipTo.id} value={shipTo.customerSequence}>
                                {shipTo.label}
                            </option>
                        ))}
                </Select>
            </GridItem>
            <GridItem {...styles.gridItemStyle}>
                <TextField
                    value={filter.orderNumber}
                    label={translate("Order # / PO #")}
                    onChange={e => {
                        changeHandler("orderNumber", e.target.value);
                    }}
                    data-test-selector="openBarckOrders_Filter_orderNumber"
                />
            </GridItem>
            <GridItem {...styles.gridItemStyle}>
                <TextField
                    value={filter.releaseNumber}
                    label={translate("Release #")}
                    onChange={e => {
                        changeHandler("releaseNumber", e.target.value);
                    }}
                    data-test-selector="openBarckOrders_Filter_releaseNumber"
                />
            </GridItem>
            <GridItem {...styles.gridItemStyle}>
                <TextField
                    value={filter.orderedBy}
                    label={translate("Ordered By")}
                    onChange={e => {
                        changeHandler("orderedBy", e.target.value);
                    }}
                    data-test-selector="openBarckOrders_Filter_orderedBy"
                />
            </GridItem>
            <GridItem {...styles.gridItemStyle}>
                <TextField
                    value={filter.partNumber}
                    label={translate("Part Number")}
                    onChange={e => {
                        changeHandler("partNumber", e.target.value);
                    }}
                    data-test-selector="openBarckOrders_Filter_partNumber"
                />
            </GridItem>
            <GridItem {...styles.gridItemStyle}>
                <TextField
                    value={filter.description}
                    label={translate("Description")}
                    onChange={e => {
                        changeHandler("description", e.target.value);
                    }}
                    data-test-selector="openBarckOrders_Filter_description"
                />
            </GridItem>
            <GridItem {...styles.buttonsItem}>
                <Button
                    {...styles.clearFiltersButton}
                    onClick={clearFilters}
                    data-test-selector="openBarckOrders_clearFiltersButton"
                    aria-label="Clear Filters"
                >
                    {translate("Clear Filters")}
                </Button>
            </GridItem>
        </GridContainer>
    );
};

const widgetModule: WidgetModule = {
    component: connect(mapStateToProps, mapDispatchToProps)(OpenBackOrdersFilter),
    definition: {
        group: XNGAGE_ELEMENTS,
        allowedContexts: ["OpenBackOrdersPage"],
        fieldDefinitions: [
            {
                name: fields.showTagsOnly,
                editorTemplate: "CheckboxField",
                defaultValue: false,
                fieldType: "General",
                sortOrder: 1,
            },
        ],
    },
};

export default widgetModule;
